<template>
  <div
    :class="{
      'ain-Wrapper-readonly': readonly,
      'ain-Wrapper-column-layout': columnLayout
    }"
    class="ain-Wrapper"
  >
    <label v-if="label" :for="`input-${inputUid}`" class="ain-Label">
      {{ label }}
    </label>
    <div :class="classes">
      <input
        :id="`input-${inputUid}`"
        ref="input-reference"
        :readonly="readonly"
        class="ain-Input"
        v-bind="bindingAttrs"
        @blur="onBlur"
        @focus="onFocus"
        @keydown.enter="onKeyEnter"
        @keydown.esc="onKeyEsc"
      />
      <span v-if="hasArrowControls" class="ain-Arrows">
        <AppButton
          v-for="action in ARROWS_ACTIONS"
          :key="action.id"
          :disable="isArrowDisabled(action.id)"
          class="ain-Arrows_Button"
          height="12"
          icon="input-number-arrow"
          remove-padding
          type="default"
          width="24"
          @click="onArrowClick(action.id)"
        />
      </span>
      <ClearButton
        v-else-if="hasClearAction"
        v-visible="!readonly && modelValue !== ''"
        class="ain-ClearButton"
        @click="onClear"
      />
    </div>
    <span v-if="isShowPreviousValue()" class="ain-PreviousValue">
      {{ displayPreviousValue }}
    </span>
  </div>
</template>

<script setup>
import IMask from 'imask'
import { clamp, debounce, isNull, isNumber, isUndefined } from 'lodash'
import {
  onMounted,
  ref,
  computed,
  onBeforeUnmount,
  nextTick,
  watch,
  useAttrs,
  useTemplateRef
} from 'vue'
import { useI18n } from 'vue-i18n'

import { useTabindexAttrs } from '@/composables/tabindex'
import { isStringEmpty } from '@/utils/general'
import { uid } from '@/utils/uid'

import AppButton from '@/components/ui/AppButton/AppButton'
import ClearButton from '@/components/ui/ClearButton/ClearButton'

defineOptions({
  name: 'AppInputNumberNext'
})

const inputReference = useTemplateRef('input-reference')

const props = defineProps({
  fraction: {
    type: Number,
    default: 0
  },

  digitMaxLength: {
    type: Number,
    default: 4
  },

  modelValue: {
    type: [Number, String],
    required: true
  },

  label: {
    type: String,
    default: ''
  },

  readonly: {
    type: Boolean
  },

  suffix: {
    type: String,
    default: ''
  },

  prefix: {
    type: String,
    default: ''
  },

  returnValueAsString: {
    type: Boolean
  },

  columnLayout: {
    type: Boolean
  },

  allowNegative: {
    type: Boolean
  },

  max: {
    type: Number,
    default: null
  },

  min: {
    type: Number,
    default: null
  },

  hasClearAction: {
    type: Boolean
  },

  hasArrowControls: {
    type: Boolean
  },

  arrowControlsStep: {
    type: Number,
    default: 1
  },

  debounceTimeout: {
    type: Number,
    default: 0,
    validator: v => v >= 0 && Number.isSafeInteger(v) && Number.isFinite(v) && !Number.isNaN(v)
  },

  allowEmptyValue: {
    type: Boolean
  },

  autoFocus: {
    type: Boolean
  },

  separateSuffix: {
    type: Boolean,
    default: true
  },

  showPreviousValue: {
    type: Boolean
  },

  fullWidth: {
    type: Boolean
  }
})

const ARROWS_ACTIONS = {
  INCREASE: {
    id: '+'
  },
  DECREASE: {
    id: '-'
  }
}

const inputUid = uid()

const classes = computed(() => {
  return {
    'ain-InputWrapper': true,
    // 'ain-InputWrapper-error': showError.value,
    'ain-InputWrapper-full-width': props.fullWidth,
    'ain-InputWrapper-readonly': props.readonly,
    'ain-InputWrapper-with-action': props.hasClearAction || props.hasArrowControls
  }
})

const onBlur = () => {
  setInputValue()
  emit('blur')
}

const onFocus = () => {
  const value = mask.value.unmaskedValue ? mask.value.unmaskedValue : props.modelValue

  const valueAsString = isNumber(value) ? String(value) : value

  const isValueCanBeEmpty = props.allowEmptyValue && (isNull(value) || isStringEmpty(valueAsString))

  if (Number(value) === 0 && !isValueCanBeEmpty) {
    nextTick(() => {
      selectAll()
    })
  }
}

const focus = () => {
  inputReference.value.focus()
}

const selectAll = () => {
  inputReference.value.select()
}

const updateMask = () => setInputValue()

defineExpose({
  focus,
  selectAll,
  updateMask,
  onFocus
})

const onKeyEnter = () => {
  inputReference.value.blur()
}

const onKeyEsc = () => {
  inputReference.value.blur()
}

const previousValue = ref(null)

const { t } = useI18n()

const displayPreviousValue = computed(() => {
  return `${t('common.previous_value')}: ${previousValue.value}`
})

const setInputValue = ({ savePreviousValue = false } = {}) => {
  // create other instance of IMask to get correct masked value with prefix, suffix, separators, etc.
  const maskHandler = IMask.createMask(maskConfiguration.value)
  let valueForResolve = `${escapedPrefix.value}${props.modelValue}${escapedSuffix.value}`

  if (props.modelValue === '' || isNull(props.modelValue)) {
    if (escapedPrefix.value && !escapedSuffix.value.trim()) {
      // that need to set focus in correct position
      // like USD | ; not USD|
      valueForResolve = `${escapedPrefix.value}`
      maskHandler.resolve(valueForResolve)
    }

    if (!escapedPrefix.value && !escapedSuffix.value.trim()) {
      maskHandler.resolve('')
    }
  } else {
    maskHandler.resolve(`${props.modelValue}`)
  }

  if (!isNull(inputReference.value)) {
    inputReference.value.value = maskHandler.value
    mask.value?.updateValue()
    if (
      savePreviousValue &&
      isNull(previousValue.value) &&
      !isStringEmpty(maskHandler.unmaskedValue)
    ) {
      previousValue.value = maskHandler.value
    }
  }
}

const isShowPreviousValue = () => {
  if (
    props.readonly ||
    !props.showPreviousValue ||
    isNull(previousValue.value) ||
    isStringEmpty(String(previousValue.value))
  ) {
    return false
  }

  return previousValue.value !== mask.value?.value
}

/**
 * Escapes all characters in a string for use in an IMask pattern.
 *
 * IMask reserves certain characters (e.g., `a`, `0`, `*`, `[`, `]`, `{`, `}`, `\`, etc.)
 * for special purposes in pattern masks. This function ensures that any character in
 * the input string is treated as a literal by escaping it with a backslash (`\`).
 *
 * Example usage with IMask:
 * ```javascript
 * const escapedString = escapeString('0a*[]{}');
 * IMask.PatternMasked({
 *   mask: escapedString
 * });
 * ```
 *
 * @param {string} [str=''] - The input string to escape. If no string is provided, defaults to an empty string.
 * @returns {string} - The escaped string with all characters prefixed by `\`.
 *
 * @example
 * escapeString('0a*[]{}'); // Returns "\\0\\a\\*\\[\\]\\{\\}"
 * escapeString('Hello');  // Returns "\\H\\e\\l\\l\\o"
 * escapeString();         // Returns ""
 */
const escapeString = (str = '') => {
  return `${str.replace(/[^-+.]/g, '\\$&')}`
}

const escapedPrefix = computed(() => {
  const prefix = escapeString(props.prefix || '')

  return prefix ? `${prefix} ` : ''
})

const escapedSuffix = computed(() => {
  const suffix = escapeString(props.suffix || '')

  return props.separateSuffix ? ` ${suffix}` : suffix
})

watch([escapedSuffix, escapedPrefix], ([newSuffix, newPrefix]) => {
  mask.value.updateOptions({
    mask: `${newPrefix}num${newSuffix}`.trim()
  })
  setInputValue()
})

watch(
  () => props.modelValue,
  () => {
    if (props.readonly) {
      updateMask()
    }
  }
)

watch(
  () => props.readonly,
  (newValue, oldValue) => {
    if (newValue !== oldValue && !isUndefined(oldValue)) {
      updateMask()
    }
  }
)

const mask = ref(null)

const getMaxDigit = num => Number(`1e${num}`) - 1

const calculateMin = ({ maxDigit, maxFraction }) => Number(`-${maxDigit}.${maxFraction}`)

const emit = defineEmits({ 'update:modelValue': null, blur: null })

const getMinValueForSpecifiedMinProp = ({ maxDigit, maxFraction }) => {
  if (props.allowNegative && props.min >= 0) {
    throw new Error('min prop must be negative if allowNegative is true')
  }

  if (!props.allowNegative && props.min < 0) {
    throw new Error('min prop must be positive if allowNegative is false')
  }

  const calculatedMin = calculateMin({ maxDigit, maxFraction })

  if (props.min < calculatedMin) {
    throw new Error(
      `min prop must be greater than ${calculatedMin} depending on digitMaxLength and fraction props`
    )
  }

  return Number(props.min)
}

const getMinValueForNotSpecifiedMinProp = ({ maxDigit, maxFraction }) => {
  if (props.allowNegative) {
    return Number(`-${maxDigit}.${maxFraction}`)
  }

  return 0
}

const MAP_TO_RADIX_SYMBOLS = [',', '+']

const maskConfiguration = computed(() => {
  const maxDigit = getMaxDigit(props.digitMaxLength)
  const maxFraction = getMaxDigit(props.fraction)

  let min

  if (!isNull(props.min)) {
    min = getMinValueForSpecifiedMinProp({ maxDigit, maxFraction })
  } else {
    min = getMinValueForNotSpecifiedMinProp({ maxDigit, maxFraction })
  }

  let max = Number(`${maxDigit}.${maxFraction}`)

  if (!isNull(props.max)) {
    max = props.max > min ? props.max : max
  }

  return {
    lazy: false,
    mask: `${escapedPrefix.value}num${escapedSuffix.value}`.trim(),
    blocks: {
      num: {
        mask: Number,
        // other options are optional with defaults below
        scale: props.fraction, // digits after point, 0 for integers
        signed: props.allowNegative, // disallow negative
        thousandsSeparator: ' ', // any single char
        padFractionalZeros: false, // if true, then pads zeros at end to the length of scale
        normalizeZeros: true, // appends or removes zeros at ends
        radix: '.', // fractional delimiter
        mapToRadix: props.allowNegative ? MAP_TO_RADIX_SYMBOLS : [...MAP_TO_RADIX_SYMBOLS, '-'], // symbols to process as radix
        max,
        min
      }
    }
  }
})

const initMask = () => {
  setInputValue({
    savePreviousValue: true
  })
  mask.value = IMask(inputReference.value, maskConfiguration.value)

  mask.value.on('accept', () => {
    if (props.debounceTimeout >= 200) {
      onDebouncedUpdateModelValue()
    } else {
      onUpdateModelValue()
    }
  })
}

const onUpdateModelValue = () => {
  let value = mask.value.unmaskedValue ? mask.value.unmaskedValue : 0

  const isEmptyValue = props.allowEmptyValue && mask.value.unmaskedValue === ''

  if (isEmptyValue) {
    value = ''
  }

  if (props.returnValueAsString) {
    emit('update:modelValue', value)
  } else {
    const resolvedValue = isEmptyValue ? value : Number(value)
    emit('update:modelValue', resolvedValue)
  }
}

const onDebouncedUpdateModelValue = debounce(onUpdateModelValue, props.debounceTimeout)

const onClear = async () => {
  emit('update:modelValue', '')
  await nextTick()
  setInputValue()

  focus()
}

const getNewValue = arrowId => {
  const currentValue = mask.value.unmaskedValue ? mask.value.unmaskedValue : props.modelValue

  const direction = Math.sign(Number(`${arrowId}1`))

  return Number(currentValue) + direction * props.arrowControlsStep
}

const isArrowDisabled = id => {
  if (!mask.value) {
    return true
  }

  if (props.readonly) {
    return true
  }

  const { min, max } = maskConfiguration.value.blocks.num

  const newValue = getNewValue(id)

  return newValue < min || newValue > max
}

const onArrowClick = id => {
  const { min, max } = maskConfiguration.value.blocks.num

  let newValue = getNewValue(id)

  newValue = clamp(newValue, min, max)

  if (props.returnValueAsString) {
    emit('update:modelValue', String(newValue))
  } else {
    emit('update:modelValue', newValue)
  }

  nextTick(() => {
    setInputValue()
    focus()
  })
}

onMounted(() => {
  initMask()

  if (!props.readonly && props.autoFocus) {
    focus()
  }
})

onBeforeUnmount(() => {
  mask.value.destroy()
})

const attrs = useAttrs()

const tabindexAttrs = useTabindexAttrs(() => props.readonly)

const bindingAttrs = computed(() => {
  const DATA_TEST_ID = 'data-testid'
  const DATA_AUTO_TEST_ID = 'data-auto-testid'
  const INPUT_ELEMENT = 'input-element'
  return {
    ...attrs,
    [DATA_TEST_ID]: attrs[DATA_TEST_ID] ? `${attrs[DATA_TEST_ID]}-${INPUT_ELEMENT}` : null,

    [DATA_AUTO_TEST_ID]: attrs[DATA_AUTO_TEST_ID]
      ? `${attrs[DATA_AUTO_TEST_ID]}-${INPUT_ELEMENT}`
      : null,
    ...tabindexAttrs.value
  }
})
</script>

<style lang="scss" scoped>
.ain-Wrapper {
  gap: 6px;
  font-family: $system-ui;
  max-width: 100%;
  position: relative;

  &:not(&-column-layout) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &-column-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
  }
}
.ain-Label {
  font-weight: fw('bold');
  font-size: $fs-12;
  line-height: 16px;
  color: $dark-3;
  .ain-Wrapper-readonly & {
    color: $grey-1-next;
  }
}

.ain-Input {
  outline: none;
  font-family: inherit;
  color: $dark-1;
  font-style: normal;
  font-weight: var(--font-weight, fw('semi-bold'));
  font-size: var(--font-size, #{$fs-20});
  height: 100%;
  border: none;
  padding: 0 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: transparent;
  width: 100%;
  caret-color: $primary-color-next;

  &:read-only {
    color: $dark-3;
  }
}

.ain-InputWrapper {
  // overflow: hidden;
  border: 2px solid $grey-2-next;
  border-radius: $border-radius-sm-next;
  transition: border-color $transition-fast ease-in-out,
    background-color $transition-fast ease-in-out;
  height: 40px;
  background-color: $white;
  display: flex;
  align-items: center;
  position: relative;
  box-sizing: border-box;

  &-full-width {
    width: 100%;
  }

  &-error {
    border-color: var(--grade-low-color-next);
  }

  &-readonly {
    border-color: $grey-2-next;
    background: $grey-2-next;
  }

  &-with-action {
    padding-right: 6px;
  }
}

.ain-InputWrapper:not(.ain-InputWrapper-error):not(.ain-InputWrapper-readonly) {
  &:focus-within {
    border-color: $primary-color-next;
    background-color: $white;
  }
}

.ain-InputWrapper:not(.ain-InputWrapper-error):not(.ain-InputWrapper-readonly):not(:focus-within) {
  &:hover {
    border-color: $grey-1-next;
    background-color: $grey-3-next;
  }
}

.ain-Error {
  position: absolute;
  right: -20px;
  display: flex;
}

.ain-Error_Icon {
  display: flex;
}

.ain-Error_Content {
  padding: 12px;
  font-size: $fs-12;
  font-family: $system-ui;
  line-height: 1.2;
}

.ain-ErrorsList {
  margin: 0;
  list-style: none;
}

.ain-ClearButton {
  flex-shrink: 0;
}

.ain-Arrows {
  flex-shrink: 0;
  height: 100%;
  width: 24px;
  display: flex;
  flex-direction: column;
}

.ain-Arrows_Button {
  height: 50%;
  width: 100%;
  display: flex;
  align-items: flex-end;

  + .ain-Arrows_Button {
    transform: rotate(180deg);
  }
}

.ain-PreviousValue {
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translate(-50%, calc(100% + 6px));
  background-color: $dark-1;
  color: $white;
  min-width: 100%;
  padding: 4px 16px;
  text-align: center;
  border-radius: $border-radius-sm;
  font-weight: fw('semi-bold');
  box-shadow: 0 14px 34px 0 rgba($dark-1, 0.2);
  pointer-events: none;
  z-index: 4;
  white-space: nowrap;

  &:not(.ain-Wrapper:focus-within &) {
    display: none;
  }
}
</style>
